import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { map, catchError } from 'rxjs/operators';
import { 
  AirLandShippingDocument, 
  SeaShippingDocument, 
  ApiResponse, 
  DocumentListItem,
  Region
} from '../../models/document.models';
import { TRANSPORT_COMPANIES, TransportCompany, getCompaniesByTransportType } from '../../models/transport.models';

export interface HandoverRequest {
  lading_bill: string;
  receive_carrier: string;
}

export interface CustomsClearanceResult {
  reference_no: string;       // Required - Maps to ShopeeOrder.Unique_id
  clear_type: number;         // Required - 1 = Import, 2 = Export
  op_time: number;           // Required - Unix timestamp
  op_location: string;       // Required
  event_code: string;        // Required - I02, 01, E04, H04, 07

  // Optional fields - can be omitted or empty
  operator?: string;
  img_url?: string;
  tax_base_type?: number;
  taxable_amount?: number;
  duty_rate?: number;
  duty_amount?: number;
  vat_rate?: number;
  vat_amount?: number;
  customs_clearance_fee?: number;
  tax_proof?: string;
  reason?: string;          // Required for E04 (Delay exception)
  remark?: string;
  partner_cte_access_key?: string;
  dir_number?: string;
}

export interface CustomsClearanceProgressRequest {
  cc_results: CustomsClearanceResult[];
}

@Injectable({
  providedIn: 'root'
})
export class DocumentService {
  // Make sure this URL matches the actual backend API endpoint
  
  private apiUrl = `${environment.apiUrl}/documents`;  // Confirm this matches your ASP.NET Core route

  constructor(private http: HttpClient) {}

  // Create air/land shipping document
  createAirLandDocument(document: AirLandShippingDocument): Observable<ApiResponse<any>> {
    return this.http.post<any>(`${this.apiUrl}/air-land/create`, document)
      .pipe(
        map(response => {
          console.log('Raw createAirLandDocument response:', response);
          
          // Check if response is in retcode format and convert to ApiResponse format
          if (response.hasOwnProperty('retcode')) {
            const success = response.retcode === 0;
            return {
              success: success,
              data: response.data,
              message: response.message || (success ? 'Document created successfully' : 'Failed to create document')
            };
          }
          
          // If response is already in ApiResponse format, return as-is
          if (response.hasOwnProperty('success')) {
            return response;
          }
          
          // Fallback for unexpected response format
          console.warn('Unexpected response format from createAirLandDocument:', response);
          return {
            success: true, // Assume success if we get here and no errors
            data: response,
            message: 'Document created successfully'
          };
        }),
        catchError(error => {
          console.error('Error creating air/land document:', error);
          return of({
            success: false,
            data: null,
            message: error.error?.message || error.message || 'Failed to create document. Please try again.'
          });
        })
      );
  }

  // Update air/land shipping document
  updateAirLandDocument(document: AirLandShippingDocument): Observable<ApiResponse<any>> {
    return this.http.put<any>(`${this.apiUrl}/air-land/update`, document)
      .pipe(
        map(response => {
          // Check if response is in retcode format and convert to ApiResponse format
          if (response.hasOwnProperty('retcode')) {
            const success = response.retcode === 0;
            return {
              success: success,
              data: response.data,
              message: response.message
            };
          }
          
          // If already in ApiResponse format, return as-is
          return response;
        })
      );
  }

  // Create sea shipping document
  createSeaDocument(document: SeaShippingDocument): Observable<ApiResponse<any>> {
    return this.http.post<any>(`${this.apiUrl}/sea/create`, document)
      .pipe(
        map(response => {
          console.log('Raw createSeaDocument response:', response);
          
          // Check if response is in retcode format and convert to ApiResponse format
          if (response.hasOwnProperty('retcode')) {
            const success = response.retcode === 0;
            return {
              success: success,
              data: response.data,
              message: response.message || (success ? 'Document created successfully' : 'Failed to create document')
            };
          }
          
          // If response is already in ApiResponse format, return as-is
          if (response.hasOwnProperty('success')) {
            return response;
          }
          
          // Fallback for unexpected response format
          console.warn('Unexpected response format from createSeaDocument:', response);
          return {
            success: true, // Assume success if we get here and no errors
            data: response,
            message: 'Document created successfully'
          };
        }),
        catchError(error => {
          console.error('Error creating sea document:', error);
          return of({
            success: false,
            data: null,
            message: error.error?.message || error.message || 'Failed to create document. Please try again.'
          });
        })
      );
  }

  // Update sea shipping document
  updateSeaDocument(document: SeaShippingDocument): Observable<ApiResponse<any>> {
    return this.http.put<any>(`${this.apiUrl}/sea/update`, document)
      .pipe(
        map(response => {
          // Check if response is in retcode format and convert to ApiResponse format
          if (response.hasOwnProperty('retcode')) {
            const success = response.retcode === 0;
            return {
              success: success,
              data: response.data,
              message: response.message
            };
          }
          
          // If already in ApiResponse format, return as-is
          return response;
        })
      );
  }
  // Get document details by ID
  getDocument(id: string): Observable<ApiResponse<any>> {
    return this.http.get<any>(`${this.apiUrl}/${id}`)
      .pipe(
        map(response => {
          // Check if response is in the expected format with retcode
          if (response.hasOwnProperty('retcode') !== undefined) {
            // Convert retcode format to success format
            const success = response.retcode === 0;
            return {
              success: success,
              data: response.data,
              message: response.message
            };
          }
          // Handle both wrapped and unwrapped response formats
          else if (response.hasOwnProperty('success') && response.hasOwnProperty('data')) {
            // Response is already in ApiResponse format
            
            // Ensure proper mapping of important fields if needed
            if (response.data) {
              // Map date fields if they exist in different formats
              if (response.data.createdAtUtc && !response.data.createdAt) {
                response.data.createdAt = response.data.createdAtUtc;
              }
              if (response.data.lastUpdatedAtUtc && !response.data.updatedAt) {
                response.data.updatedAt = response.data.lastUpdatedAtUtc;
              }
              // Map carton count field if needed
              if (response.data.cartonCount && !response.data.totalCartons) {
                response.data.totalCartons = response.data.cartonCount;
              }
            }
            
            return response;
          } else {
            // Convert direct object to ApiResponse format
            // Map date and count fields if they exist in different formats
            if (response) {
              if (response.createdAtUtc && !response.createdAt) {
                response.createdAt = response.createdAtUtc;
              }
              if (response.lastUpdatedAtUtc && !response.updatedAt) {
                response.updatedAt = response.lastUpdatedAtUtc;
              }
              if (response.cartonCount && !response.totalCartons) {
                response.totalCartons = response.cartonCount;
              }
            }
            
            return {
              success: true,
              data: response,
              message: 'Document retrieved successfully'
            };
          }
        }),
        catchError(error => {
          console.error('Error fetching document details', error);
          return of({
            success: false,
            data: null,
            message: error.message || 'Error retrieving document'
          });
        })
      );
  }  // Get all documents with optional filtering
  getDocuments(filters?: any): Observable<ApiResponse<DocumentListItem[]>> {
    let params = new HttpParams();
    
    if (filters) {
      Object.keys(filters).forEach(key => {
        if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
          params = params.append(key, filters[key]);
        }
      });
    }
    
    return this.http.get<any>(`${this.apiUrl}`, { params })
      .pipe(
        map(response => {
          console.log('Raw documents response:', response);
          
          // Handle both wrapped and unwrapped response formats
          if (response.hasOwnProperty('success') && response.hasOwnProperty('data')) {
            // Response is already in ApiResponse format
            return response;
          } else if (response.hasOwnProperty('Items') || response.hasOwnProperty('items')) {
            // Check for both camelCase 'items' (from backend) or PascalCase 'Items'
            const items = response.items || response.Items || [];
            
            // Map the documents to the expected frontend format if needed
            const mappedItems = items.map((item: any) => {
              // Create a properly formatted DocumentListItem
              return {
                id: item.id,
                ladingBill: item.ladingBill || item.landing_bill,
                documentType: item.documentType,
                status: item.status,
                departureRegion: item.departureRegion || item.departure_region,
                arrivalRegion: item.destinationRegion || item.arrivalRegion || item.arrival_region,
                createdAtUtc: item.createdAtUtc || item.created_at_utc,
                lastUpdatedAtUtc: item.lastUpdatedAtUtc || item.last_updated_at_utc,
                cartonCount: item.cartonCount || item.total_cartons || (item.carton_list ? item.carton_list.length : 0),
                totalContainers: item.totalContainers || item.containerCount || item.total_containers
              };
            });
            
            return {
              success: true,
              data: mappedItems,
              message: 'Documents retrieved successfully'
            };
          } else if (Array.isArray(response)) {
            // Direct array response
            const mappedItems = response.map((item: any) => {
              return {
                id: item.id,
                ladingBill: item.ladingBill || item.landing_bill,
                documentType: item.documentType,
                status: item.status,
                departureRegion: item.departureRegion || item.departure_region,
                arrivalRegion: item.destinationRegion || item.arrivalRegion || item.arrival_region,
                createdAtUtc: item.createdAtUtc || item.created_at_utc,
                lastUpdatedAtUtc: item.lastUpdatedAtUtc || item.last_updated_at_utc,
                cartonCount: item.cartonCount || item.total_cartons || (item.carton_list ? item.carton_list.length : 0),
                totalContainers: item.totalContainers || item.containerCount || item.total_containers
              };
            });
            
            return {
              success: true,
              data: mappedItems,
              message: 'Documents retrieved successfully'
            };
          } else {
            console.error('Unexpected response format from documents endpoint', response);
            return {
              success: false,
              data: [],
              message: 'Error retrieving documents'
            };
          }
        }),
        catchError(error => {
          console.error('Error fetching documents', error);
          return of({
            success: false,
            data: [],
            message: error.message || 'Error retrieving documents'
          });
        })
      );
  }

  // Get eligible cartons for shipping documents, shipping progress, or customs clearance
  getEligibleCartons(filters?: any, isForShippingProgress?: boolean, isForCustomsClearanceInfo?: boolean): Observable<any> {
    let params = new HttpParams();
    
    // Add the isForShippingProgress parameter if specified
    if (isForShippingProgress !== undefined) {
      params = params.append('isForShippingProgress', isForShippingProgress.toString());
    }

    // Add the isForCustomsClearanceInfo parameter if specified
    if (isForCustomsClearanceInfo !== undefined) {
      params = params.append('isForCustomsClearanceInfo', isForCustomsClearanceInfo.toString());
    }
    
    if (filters) {
      Object.keys(filters).forEach(key => {
        if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
          params = params.append(key, filters[key]);
        }
      });
    }
    
    return this.http.get<any>(`${this.apiUrl}/available-cartons`, { params })
      .pipe(
        map(response => {
          console.log('Raw cartons response:', response);
          
          // Handle both wrapped and unwrapped response formats
          if (response.hasOwnProperty('success') && response.hasOwnProperty('data')) {
            // Response is wrapped in ApiResponse format
            return response;
          } else if (response.hasOwnProperty('items') || response.hasOwnProperty('Items')) {
            // Check for camelCase 'items' (from backend) or PascalCase 'Items' 
            const items = response.items || response.Items || [];
            
            // Map the backend fields to the expected format for the frontend
            const mappedItems = items.map((item: any) => ({
              carrierTrackingNumber: item.carrierTrackingNumber || item.carrier_tn,
              orderId: item.orderId || item.order_id,
              weight: item.weight,
              length: item.length,
              width: item.width,
              height: item.height,
              status: item.status,
              repackedDate: item.repackedDate || item.repacked_date,
              destinationRegion: item.destinationRegion,
              destinationRegionName: item.destinationRegionName,
              // Additional fields for UI display
              displayName: item.carrierTrackingNumber || item.carrier_tn,
              dimensions: `${item.length || 0}x${item.width || 0}x${item.height || 0}`
            }));
            
            return {
              success: true,
              data: {
                items: mappedItems,
                totalCount: response.totalCount || response.TotalCount || items.length,
                pageSize: response.pageSize || response.PageSize || items.length,
                pageNumber: response.pageNumber || response.PageNumber || 1,
                totalPages: response.totalPages || response.TotalPages || 1
              },
              message: 'Available cartons retrieved successfully'
            };
          } else {
            // Unexpected format
            console.error('Unexpected response format from available-cartons endpoint', response);
            return {
              success: false,
              data: null,
              message: 'Error retrieving available cartons'
            };
          }
        }),
        catchError(error => {
          console.error('Error fetching available cartons', error);
          return of({
            success: false,
            data: null,
            message: error.message || 'Error retrieving available cartons'
          });
        })
      );
  }
  // Get regions for reference data (as a fallback in case hardcoded data isn't sufficient)
  getRegions(): Observable<ApiResponse<Region[]>> {
    return this.http.get<any>(`${this.apiUrl}/regions`)
      .pipe(
        map(response => {
          console.log('Raw regions response:', response);
          
          // Handle both wrapped and unwrapped response formats
          if (response.hasOwnProperty('success') && response.hasOwnProperty('data')) {
            // Response is already in ApiResponse format
            return response;
          } else if (Array.isArray(response)) {
            // Map the regions to ensure they have code and name properties
            const mappedRegions = response.map(region => ({
              code: region.code || region.alpha2Code || region.id || '',
              name: region.name || region.countryName || '',
              isActive: region.isActive !== false
            }));
            
            return {
              success: true,
              data: mappedRegions,
              message: 'Regions retrieved successfully'
            };
          } else {
            console.error('Unexpected response format from regions endpoint', response);
            return {
              success: false,
              data: [],
              message: 'Error retrieving regions'
            };
          }
        }),
        catchError(error => {
          console.error('Error fetching regions', error);
          return of({
            success: false,
            data: [],
            message: error.message || 'Error retrieving regions'
          });
        })
      );
  }
  // Get document statuses/history
  getDocumentHistory(id: string): Observable<ApiResponse<any>> {
    // First, try to get the history from a dedicated endpoint
    return this.http.get<any>(`${this.apiUrl}/${id}/history`)
      .pipe(
        map(response => {
          console.log('Document history response:', response);
          
          // Handle retcode format (which your backend is using)
          if (response.hasOwnProperty('retcode')) {
            const success = response.retcode === 0;
            return {
              success: success,
              data: response.data,
              message: response.message || (success ? 'Document history retrieved successfully' : 'Failed to retrieve document history')
            };
          }
          // Handle ApiResponse format
          else if (response.hasOwnProperty('success') && response.hasOwnProperty('data')) {
            return response;
          } 
          // Handle direct object response
          else {
            return {
              success: true,
              data: response,
              message: 'Document history retrieved successfully'
            };
          }
        }),
        catchError(error => {
          console.error('Error fetching document history:', error);
          
          // If history endpoint fails (404), fallback to creating history from main document data
          if (error.status === 404) {
            return this.getDocument(id).pipe(
              map(docResponse => {
                if (docResponse.success && docResponse.data) {
                  const doc = docResponse.data;
                  
                  // Create mock history data from document information
                  const historyData = {
                    id: doc.id,
                    ladingBill: doc.ladingBill || doc.landing_bill || 'N/A',
                    documentType: this.getDocumentTypeLabel(doc.documentType || doc.document_type),
                    status: this.getStatusLabel(doc.status),
                    createdAtUtc: doc.createdAtUtc || doc.createdAt || doc.created_at,
                    lastUpdatedAtUtc: doc.lastUpdatedAtUtc || doc.updatedAt || doc.updated_at,
                    secondLastUpdatedAtUtc: null, // We don't have this info
                    lastModifiedBy: 'System', // Default value since we don't have this info
                    lastUpdateDetails: 'Document was last updated with current information',
                    cartonCount: doc.totalCartons || doc.cartonCount || (doc.carton_list ? doc.carton_list.length : 0),
                    departureRegion: doc.departureRegion || doc.departure_region || 'N/A',
                    destinationRegion: doc.destinationRegion || doc.destination_region || 'N/A'
                  };
                  
                  return {
                    success: true,
                    data: historyData,
                    message: 'Document history created from available data'
                  };
                } else {
                  return {
                    success: false,
                    data: null,
                    message: 'Unable to load document history'
                  };
                }
              }),
              catchError(() => of({
                success: false,
                data: null,
                message: 'Document history endpoint not available and unable to load document data'
              }))
            );
          }
          
          return of({
            success: false,
            data: null,
            message: error.error?.message || error.message || 'Error retrieving document history'
          });
        })
      );
  }

  // Helper method to get document type label
  private getDocumentTypeLabel(type: number): string {
    switch (type) {
      case 1: return 'Air/Land';
      case 2: return 'Sea';
      default: return 'Unknown';
    }
  }

  // Helper method to get status label
  private getStatusLabel(status: number): string {
    switch (status) {
      case 0: return 'Created';
      case 1: return 'Updated';
      case 2: return 'Confirmed';
      case 3: return 'Failed';
      case 4: return 'Cancelled';
      case 5: return 'Handed Over';
      default: return 'Unknown';
    }
  }

  // Get transport company by code
  getTransportCompanyByCode(code: string): TransportCompany | undefined {
    return TRANSPORT_COMPANIES.find(company => company.code === code);
  }

  // Get transport type name
  getTransportTypeName(type: number): string {
    switch (type) {
      case 1: return 'Air';
      case 2: return 'Land';
      case 3: return 'Sea';
      default: return 'Unknown';
    }
  }

  // Get eligible lading bills for bulk shipping progress updates
  getEligibleLadingBills(filters?: any): Observable<any> {
    let params = new HttpParams();
    
    if (filters) {
      Object.keys(filters).forEach(key => {
        if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
          params = params.append(key, filters[key]);
        }
      });
    }
    
    return this.http.get<any>(`${this.apiUrl}/eligible-lading-bills`, { params })
      .pipe(
        map(response => {
          console.log('Raw lading bills response:', response);
          
          // Handle both wrapped and unwrapped response formats
          if (response.hasOwnProperty('success') && response.hasOwnProperty('data')) {
            // Response is wrapped in ApiResponse format
            return response;
          } else if (response.hasOwnProperty('items') || response.hasOwnProperty('Items')) {
            // Check for camelCase 'items' (from backend) or PascalCase 'Items' 
            const items = response.items || response.Items || [];
            
            // Map the backend fields to the expected format for the frontend
            const mappedItems = items.map((item: any) => ({
              id: item.id,
              ladingBill: item.ladingBill || item.landing_bill,
              documentType: item.documentType || item.document_type || 0,
              status: item.status,
              departureRegion: item.departureRegion || item.departure_region,
              arrivalRegion: item.destinationRegion || item.arrivalRegion || item.arrival_region,
              createdAtUtc: item.createdAtUtc || item.created_at_utc,
              lastUpdatedAtUtc: item.lastUpdatedAtUtc || item.last_updated_at_utc,
              cartonCount: item.cartonCount || item.carton_count || 0,
              cartons: (item.cartons || item.carton_list || []).map((carton: any) => ({
                carrierTrackingNumber: carton.carrierTrackingNumber || carton.carrier_tn,
                orderId: carton.orderId || carton.order_id,
                weight: carton.weight,
                length: carton.length,
                width: carton.width,
                height: carton.height,
                status: carton.status,
                repackedDate: carton.repackedDate || carton.repacked_date,
                displayName: carton.carrierTrackingNumber || carton.carrier_tn,
                dimensions: `${carton.length || 0}x${carton.width || 0}x${carton.height || 0}`
              }))
            }));
            
            return {
              success: true,
              data: {
                items: mappedItems,
                totalCount: response.totalCount || response.TotalCount || items.length,
                pageSize: response.pageSize || response.PageSize || items.length,
                pageNumber: response.pageNumber || response.PageNumber || 1,
                totalPages: response.totalPages || response.TotalPages || 1
              },
              message: 'Eligible lading bills retrieved successfully'
            };
          } else if (Array.isArray(response)) {
            // Direct array response
            const mappedItems = response.map((item: any) => ({
              id: item.id,
              ladingBill: item.ladingBill || item.landing_bill,
              documentType: item.documentType || item.document_type || 0,
              status: item.status,
              departureRegion: item.departureRegion || item.departure_region,
              arrivalRegion: item.destinationRegion || item.arrivalRegion || item.arrival_region,
              createdAtUtc: item.createdAtUtc || item.created_at_utc,
              lastUpdatedAtUtc: item.lastUpdatedAtUtc || item.last_updated_at_utc,
              cartonCount: item.cartonCount || item.total_cartons || 0,
              cartons: (item.cartons || item.carton_list || []).map((carton: any) => ({
                carrierTrackingNumber: carton.carrierTrackingNumber || carton.carrier_tn,
                orderId: carton.orderId || carton.order_id,
                weight: carton.weight,
                length: carton.length,
                width: carton.width,
                height: carton.height,
                status: carton.status,
                repackedDate: carton.repackedDate || carton.repacked_date,
                displayName: carton.carrierTrackingNumber || carton.carrier_tn,
                dimensions: `${carton.length || 0}x${carton.width || 0}x${carton.height || 0}`
              }))
            }));
            
            return {
              success: true,
              data: {
                items: mappedItems,
                totalCount: response.length,
                pageSize: response.length,
                pageNumber: 1,
                totalPages: 1
              },
              message: 'Eligible lading bills retrieved successfully'
            };
          } else {
            console.warn('Unexpected response format for lading bills:', response);
            return {
              success: false,
              data: { items: [], totalCount: 0, pageSize: 10, pageNumber: 1, totalPages: 0 },
              message: 'Unexpected response format'
            };
          }
        }),
        catchError(error => {
          console.error('Error loading eligible lading bills:', error);
          return of({
            success: false,
            data: { items: [], totalCount: 0, pageSize: 10, pageNumber: 1, totalPages: 0 },
            message: error.error?.message || error.message || 'Failed to load eligible lading bills'
          });
        })
      );
  }

  // Get eligible documents for handover
  getHandoverEligibleDocuments(): Observable<any> {
     return this.http.get<any>(`${this.apiUrl}/handover-eligible`)
      .pipe(
        map(response => {
          console.log('Raw handover eligible documents response:', response);
          
          // Handle response format
          if (response.hasOwnProperty('success') && response.hasOwnProperty('data')) {
            // Response is wrapped in ApiResponse format
            return response;
          } else if (response.hasOwnProperty('items') || response.hasOwnProperty('Items')) {
            // Paginated response format from backend
            const items = response.items || response.Items || [];
            
            // Map the backend fields to the expected format for the frontend
            const mappedItems = items.map((item: any) => ({
              id: item.id,
              ladingBill: item.ladingBill || item.landing_bill,
              documentId: item.id, // Use id as documentId for display
              orderId: item.orderId || 'N/A', // May not be available in this response
              destinationRegion: item.destinationRegion || item.arrivalRegion,
              status: item.status, // Keep as number for proper status handling
              createdDate: item.createdAtUtc || item.created_at_utc,
              carrierTrackingNumber: item.carrierTrackingNumber || null,
              documentType: item.documentType,
              departureRegion: item.departureRegion,
              cartonCount: item.cartonCount
            }));
            
            return {
              success: true,
              data: mappedItems,
              message: 'Handover eligible documents retrieved successfully'
            };
          } else if (Array.isArray(response)) {
            // Direct array response
            const mappedItems = response.map((item: any) => ({
              id: item.id,
              ladingBill: item.ladingBill || item.landing_bill,
              documentId: item.id,
              orderId: item.orderId || 'N/A',
              destinationRegion: item.destinationRegion || item.arrivalRegion,
              status: item.status, // Keep as number for proper status handling
              createdDate: item.createdAtUtc || item.created_at_utc,
              carrierTrackingNumber: item.carrierTrackingNumber || null,
              documentType: item.documentType,
              departureRegion: item.departureRegion,
              cartonCount: item.cartonCount
            }));
            
            return {
              success: true,
              data: mappedItems,
              message: 'Handover eligible documents retrieved successfully'
            };
          } else {
            // Unexpected format
            console.error('Unexpected response format from handover-eligible endpoint', response);
            return {
              success: false,
              data: [],
              message: 'Error retrieving handover eligible documents'
            };
          }
        }),
        catchError(error => {
          console.error('Error fetching handover eligible documents', error);
          return of({
            success: false,
            data: [],
            message: error.message || 'Error retrieving handover eligible documents'
          });
        })
      );
  }

  // Submit batch handover for multiple documents
  submitBatchHandover(requests: HandoverRequest[]): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/api/Documents/batch-handover`, requests)
      .pipe(
        map(response => {
          console.log('Raw batch handover response:', response);
          
          // Handle response format
          if (response.hasOwnProperty('success')) {
            return response;
          } else {
            // If response doesn't have success property, assume success
            return {
              success: true,
              data: response,
              message: 'Batch handover processed successfully'
            };
          }
        }),
        catchError(error => {
          console.error('Error submitting batch handover', error);
          return of({
            success: false,
            data: null,
            message: error.error?.message || error.message || 'Error processing batch handover'
          });
        })
      );
  }

  // Get handover history
  getHandoverHistory(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/handover-history`)
      .pipe(
        map(response => {
          console.log('Raw handover history response:', response);
          
          // Handle response format
          if (response.hasOwnProperty('success') && response.hasOwnProperty('data')) {
            // Response is wrapped in ApiResponse format
            return response;
          } else if (response.hasOwnProperty('items') || response.hasOwnProperty('Items')) {
            // Paginated response format from backend
            const items = response.items || response.Items || [];
            
            // Map the backend fields to the expected format for the frontend
            const mappedItems = items.map((item: any) => ({
              id: item.id,
              ladingBill: item.ladingBill || item.landing_bill,
              documentId: item.id,
              orderId: item.orderId || 'N/A',
              destinationRegion: item.destinationRegion || item.arrivalRegion,
              status: item.status, // Keep as number for proper status handling
              handoverDate: item.handoverDate || item.handover_date || item.lastUpdatedAtUtc,
              receiveCarrier: item.receiveCarrier || item.receive_carrier || 'N/A',
              createdDate: item.createdAtUtc || item.created_at_utc
            }));
            
            return {
              success: true,
              data: mappedItems,
              message: 'Handover history retrieved successfully'
            };
          } else if (Array.isArray(response)) {
            // Direct array response
            const mappedItems = response.map((item: any) => ({
              id: item.id,
              ladingBill: item.ladingBill || item.landing_bill,
              documentId: item.id,
              orderId: item.orderId || 'N/A',
              destinationRegion: item.destinationRegion || item.arrivalRegion,
              status: item.status,
              handoverDate: item.handoverDate || item.handover_date || item.lastUpdatedAtUtc,
              receiveCarrier: item.receiveCarrier || item.receive_carrier || 'N/A',
              createdDate: item.createdAtUtc || item.created_at_utc
            }));
            
            return {
              success: true,
              data: mappedItems,
              message: 'Handover history retrieved successfully'
            };
          } else {
            // Unexpected format
            console.error('Unexpected response format from handover-history endpoint', response);
            return {
              success: false,
              data: [],
              message: 'Error retrieving handover history'
            };
          }
        }),
        catchError(error => {
          console.error('Error fetching handover history', error);
          return of({
            success: false,
            data: [],
            message: error.message || 'Error retrieving handover history'
          });
        })
      );
  }

  // Submit single handover (for individual documents)
  submitHandover(request: HandoverRequest): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/api/Documents/handover`, request)
      .pipe(
        map(response => {
          console.log('Single handover response:', response);
          
          // Handle response format
          if (response.hasOwnProperty('success')) {
            return response;
          } else {
            // If response doesn't have success property, assume success
            return {
              success: true,
              data: response,
              message: 'Handover processed successfully'
            };
          }
        }),
        catchError(error => {
          console.error('Error submitting handover', error);
          return of({
            success: false,
            data: null,
            message: error.error?.message || error.message || 'Error processing handover'
          });
        })
      );
  }

  // Submit customs clearance progress
  submitCustomsClearanceProgress(request: CustomsClearanceProgressRequest): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/customs/clearance-progress`, request)
      .pipe(
        map(response => {
          console.log('Customs clearance progress response:', response);
          
          // Handle response format
          if (response.hasOwnProperty('success')) {
            return response;
          } else {
            // If response doesn't have success property, assume success
            return {
              success: true,
              data: response,
              message: 'Customs clearance progress processed successfully'
            };
          }
        }),
        catchError(error => {
          console.error('Error submitting customs clearance progress', error);
          return of({
            success: false,
            data: null,
            message: error.error?.message || error.message || 'Error processing customs clearance progress'
          });
        })
      );
  }
}
